import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { AsyncSelect, DatePicker, Switch } from "@neonumy/neonumy-ui";
import { Col, Divider, Form, Input, Row, Select } from "antd5";
import dayjs from "dayjs";
import { LIST_COUNTRIES_QUERY } from "queries/centralapi/centralapi";
import { useTranslation } from "react-i18next";
import { createOptionCountry } from "utils/addressOptions";
import { DATE_PICKER_POPUP_ALIGN } from "views/Collaborators/utils/datePicker";

interface SettingsUserNewraiProps {
  workShiftOptions: Option[];
  disabled: boolean;
  showCanUseBlockedSsiAccounts: boolean;
}

export default function SettingsUserNewrai({
  workShiftOptions,
  disabled,
  showCanUseBlockedSsiAccounts,
}: SettingsUserNewraiProps) {
  const { t } = useTranslation();

  const form = Form.useFormInstance();
  const startDate = Form.useWatch("startDate", form);

  const disabledDate = (current: dayjs.Dayjs, startDate: string) => {
    return current && current <= dayjs(startDate).add(1, "day");
  };

  return (
    <>
      <Divider />
      <h4>{t("Newrai user data")}</h4>
      <Row gutter={[58, 21]}>
        <Col lg={6} xs={24}>
          <Form.Item label={t("Start Date")} name="startDate">
            <DatePicker disabled={disabled} popupAlign={DATE_PICKER_POPUP_ALIGN} />
          </Form.Item>
        </Col>
        <Col lg={8} xs={24}>
          <Form.Item label={t("End Date")} name="endDate">
            <DatePicker
              disabledDate={(current: dayjs.Dayjs) => disabledDate(current, startDate)}
              disabled={disabled}
              popupAlign={DATE_PICKER_POPUP_ALIGN}
            />
          </Form.Item>
        </Col>
        <Col lg={10} xs={24}>
          <Form.Item label={t("Personal identification number")} name="registrationNumber">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col lg={6} sm={8} xs={24}>
          <Form.Item
            label={t("Country")}
            name="country"
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <AsyncSelect
              optionsFormat={createOptionCountry}
              queryOptions={{
                client: clientCentralApiStrawberry,
                fetchPolicy: "cache-first",
                dataName: "listCountriesPaginated",
                query: LIST_COUNTRIES_QUERY,
                getVariables: (search, prevOptions) => ({
                  limit: 10,
                  search,
                  offset: prevOptions.length,
                }),
              }}
              isDisabled={disabled}
              // Opens upwards when there is no room below.
              menuPlacement="auto"
            />
          </Form.Item>
        </Col>
        <Col lg={6} sm={8} xs={24}>
          <Form.Item label={t("Work shift")} name="workShift">
            <Select options={workShiftOptions} disabled={disabled} />
          </Form.Item>
        </Col>
        {showCanUseBlockedSsiAccounts && (
          <Col flex={230}>
            <Form.Item
              label={t("Can use blocked SSI accounts")}
              name="canUseBlockedSsiAccounts"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch size="small" disabled={disabled} />
            </Form.Item>
          </Col>
        )}
      </Row>
    </>
  );
}
